<template>
  <div class="auth-page">
    <div class="auth-shell">
      <div class="auth-shell__brand">
        <div class="auth-brand__header">
          <div class="auth-brand__logo">
            <img src="@/assets/logo.png" alt="BISU RIE System logo" />
          </div>
          <div class="auth-brand__label">
            <span>Digital Platform</span>
            <span>RIE System</span>
          </div>
        </div>

        <div>
          <h1 class="auth-brand__title">
            <span>Research, Innovation</span>
            <span class="accent">& Extension</span>
          </h1>
          <p class="auth-brand__description">
            Empowering BISU faculty to track, manage, and showcase research outputs, projects, and extension activities.
          </p>
        </div>

        <div class="auth-brand__stats">
          <div class="auth-brand__stat">
            <span class="auth-brand__stat-figure">500+</span>
            <span class="auth-brand__stat-caption">Research Papers</span>
          </div>
          <div class="auth-brand__stat">
            <span class="auth-brand__stat-figure">150+</span>
            <span class="auth-brand__stat-caption">Faculty Members</span>
          </div>
          <div class="auth-brand__stat">
            <span class="auth-brand__stat-figure">80+</span>
            <span class="auth-brand__stat-caption">Extension Programs</span>
          </div>
        </div>
      </div>

      <div class="auth-shell__content">
        <form class="auth-form" @submit.prevent="handleRegister">
          <button class="auth-arrow-link" type="button" @click="handleSignIn">← Back to sign in</button>

          <div class="auth-form__header">
            <h2 class="main-title">Create an account</h2>
            <p class="subtitle">Register as a new faculty member to access the RIE workspace.</p>
          </div>

          <!-- Name Fields (Side by Side) -->
          <div class="form-row form-row--split-3">
            <div class="form-group">
              <label class="form-label" for="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                class="form-input"
                v-model="form.lastName"
                placeholder="Last Name"
                :class="{ 'error': errors.lastName }"
              />
              <span v-if="errors.lastName" class="small-note error-message">
                {{ errors.lastName }}
              </span>
            </div>

            <div class="form-group">
              <label class="form-label" for="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                class="form-input"
                v-model="form.firstName"
                placeholder="First Name"
                :class="{ 'error': errors.firstName }"
              />
              <span v-if="errors.firstName" class="small-note error-message">
                {{ errors.firstName }}
              </span>
            </div>

            <div class="form-group">
              <label class="form-label" for="middleInitial">Middle Initial</label>
              <input
                type="text"
                id="middleInitial"
                class="form-input"
                v-model="form.middleInitial"
                placeholder="MI"
                maxlength="2"
                :class="{ 'error': errors.middleInitial }"
              />
              <span v-if="errors.middleInitial" class="small-note error-message">
                {{ errors.middleInitial }}
              </span>
            </div>
          </div>

          <!-- Username -->
          <div class="form-group">
            <label class="form-label" for="username">Username *</label>
            <input
              type="text"
              id="username"
              class="form-input"
              v-model="form.username"
              placeholder="Enter a username"
              :class="{ 'error': errors.username }"
            />
            <span v-if="errors.username" class="small-note error-message">
              {{ errors.username }}
            </span>
          </div>

          <!-- Contact Number -->
          <div class="form-group">
            <label class="form-label" for="contactNumber">Contact Number *</label>
            <input
              type="text"
              id="contactNumber"
              class="form-input"
              v-model="form.contactNumber"
              placeholder="e.g., 09123456789"
              :class="{ 'error': errors.contactNumber }"
            />
            <span v-if="errors.contactNumber" class="small-note error-message">
              {{ errors.contactNumber }}
            </span>
          </div>

          <!-- Email -->
          <div class="form-group">
            <label class="form-label" for="email">Email *</label>
            <input
              type="email"
              id="email"
              class="form-input"
              v-model="form.email"
              placeholder="Enter your email"
              :class="{ 'error': errors.email }"
            />
            <span v-if="errors.email" class="small-note error-message">
              {{ errors.email }}
            </span>
          </div>

          <!-- Campus -->
          <div class="form-group">
            <label class="form-label" for="campus">BISU Campus *</label>
            <select
              id="campus"
              class="form-input"
              v-model="form.campus"
              :class="{ 'error': errors.campus }"
            >
              <option value="" disabled>Select Campus</option>
              <option value="Main">Main</option>
              <option value="Bilar">Bilar</option>
              <option value="Balilihan">Balilihan</option>
              <option value="Calape">Calape</option>
              <option value="Candijay">Candijay</option>
              <option value="Clarin">Clarin</option>
            </select>
            <span v-if="errors.campus" class="small-note error-message">
              {{ errors.campus }}
            </span>
          </div>

          <!-- Password -->
          <div class="form-group">
            <label class="form-label" for="password">Password *</label>
            <div class="password-field">
              <input
                :type="showPassword ? 'text' : 'password'"
                id="password"
                class="form-input"
                v-model="form.password"
                placeholder="At least 6 characters"
                :class="{ 'error': errors.password }"
                autocomplete="new-password"
              />
              <button
                type="button"
                class="password-toggle"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                :aria-pressed="showPassword ? 'true' : 'false'"
                @click="showPassword = !showPassword"
              >
                <svg v-if="!showPassword" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M12 5c-5.5 0-9.5 4.2-11 7 1.5 2.8 5.5 7 11 7s9.5-4.2 11-7c-1.5-2.8-5.5-7-11-7zm0 12c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5zm0-8a3 3 0 100 6 3 3 0 000-6z"
                  />
                </svg>
                <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M2.3 3.7a1 1 0 011.4 0l16.6 16.6a1 1 0 01-1.4 1.4l-2.2-2.2c-1.4.7-3 .9-4.7.9-5.5 0-9.5-4.2-11-7 1-1.9 3.2-4.6 6.4-6.1L2.3 5.1a1 1 0 010-1.4zM12 7c-.8 0-1.6.2-2.3.6l1.7 1.7c.2-.1.4-.1.6-.1 1.7 0 3 1.3 3 3 0 .2 0 .4-.1.6l1.7 1.7c.4-.7.6-1.5.6-2.3 0-2.8-2.2-5-5-5zm0-2c5.5 0 9.5 4.2 11 7-.7 1.3-1.8 2.8-3.3 4.1l-1.4-1.4c1.1-1 2-2.1 2.6-2.7-1.5-2.2-4.9-5-8.9-5-1 0-2 .2-2.9.5L7.8 6.1c1.3-.7 2.7-1.1 4.2-1.1zm-8.9 7c1.5 2.2 4.9 5 8.9 5 1.1 0 2.2-.2 3.2-.6l-1.6-1.6c-.5.1-1 .2-1.6.2-2.8 0-5-2.2-5-5 0-.6.1-1.1.2-1.6L5 6.8c-1.2 1-2.1 2.2-2.7 3.2z"
                  />
                </svg>
              </button>
            </div>
            <span v-if="errors.password" class="small-note error-message">
              {{ errors.password }}
            </span>
          </div>

          <!-- Confirm Password -->
          <div class="form-group">
            <label class="form-label" for="confirmPassword">Confirm Password *</label>
            <div class="password-field">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                id="confirmPassword"
                class="form-input"
                v-model="form.confirmPassword"
                placeholder="Re-enter password"
                :class="{ 'error': errors.confirmPassword }"
                autocomplete="new-password"
              />
              <button
                type="button"
                class="password-toggle"
                :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
                :aria-pressed="showConfirmPassword ? 'true' : 'false'"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <svg v-if="!showConfirmPassword" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M12 5c-5.5 0-9.5 4.2-11 7 1.5 2.8 5.5 7 11 7s9.5-4.2 11-7c-1.5-2.8-5.5-7-11-7zm0 12c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5zm0-8a3 3 0 100 6 3 3 0 000-6z"
                  />
                </svg>
                <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M2.3 3.7a1 1 0 011.4 0l16.6 16.6a1 1 0 01-1.4 1.4l-2.2-2.2c-1.4.7-3 .9-4.7.9-5.5 0-9.5-4.2-11-7 1-1.9 3.2-4.6 6.4-6.1L2.3 5.1a1 1 0 010-1.4zM12 7c-.8 0-1.6.2-2.3.6l1.7 1.7c.2-.1.4-.1.6-.1 1.7 0 3 1.3 3 3 0 .2 0 .4-.1.6l1.7 1.7c.4-.7.6-1.5.6-2.3 0-2.8-2.2-5-5-5zm0-2c5.5 0 9.5 4.2 11 7-.7 1.3-1.8 2.8-3.3 4.1l-1.4-1.4c1.1-1 2-2.1 2.6-2.7-1.5-2.2-4.9-5-8.9-5-1 0-2 .2-2.9.5L7.8 6.1c1.3-.7 2.7-1.1 4.2-1.1zm-8.9 7c1.5 2.2 4.9 5 8.9 5 1.1 0 2.2-.2 3.2-.6l-1.6-1.6c-.5.1-1 .2-1.6.2-2.8 0-5-2.2-5-5 0-.6.1-1.1.2-1.6L5 6.8c-1.2 1-2.1 2.2-2.7 3.2z"
                  />
                </svg>
              </button>
            </div>
            <span v-if="errors.confirmPassword" class="small-note error-message">
              {{ errors.confirmPassword }}
            </span>
          </div>

          <!-- Submit -->
          <button type="submit" class="custom-button primary" :disabled="isLoading">
            <span v-if="isLoading" class="spinner"></span>
            {{ isLoading ? 'Registering...' : 'Sign Up' }}
          </button>

          <!-- Global Messages -->
          <div v-if="error || success" class="form-feedback">
            <div v-if="error" class="error-message">{{ error }}</div>
            <div v-if="success" class="success-message">{{ success }}</div>
          </div>

          <div class="auth-form__links">
            Already have an account?
            <a href="#" @click.prevent="handleSignIn">Sign in</a>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/apiClient'

export default {
  name: 'SignUp',
  data() {
    return {
      form: {
        lastName: '',
        firstName: '',
        middleInitial: '',
        username: '',
        email: '',
        campus: '',
        contactNumber: '', // <-- new contact number (string)
        password: '',
        confirmPassword: ''
      },
      errors: {},
      error: '',
      success: '',
      isLoading: false,
      showPassword: false,
      showConfirmPassword: false
    }
  },
  methods: {
    handleSignIn() {
      this.$router.push({ name: 'SignIn' })
    },

    async handleRegister() {
      this.error = ''
      this.success = ''
      this.errors = {}

      // Validation
      if (!this.form.lastName.trim()) this.errors.lastName = 'Last name is required'
      if (!this.form.firstName.trim()) this.errors.firstName = 'First name is required'
      if (!this.form.username.trim()) this.errors.username = 'Username is required'
      if (!this.form.email.trim()) this.errors.email = 'Email is required'
      if (!this.form.campus.trim()) this.errors.campus = 'Campus is required'
      if (!this.form.contactNumber.trim()) this.errors.contactNumber = 'Contact number is required'

      // Contact number numeric check (allow +, -, space)
      const phoneClean = this.form.contactNumber.replace(/[\s\-()+]/g, '')
      if (!/^\d+$/.test(phoneClean)) this.errors.contactNumber = 'Contact number must contain only digits'

      if (!this.form.password.trim()) this.errors.password = 'Password is required'
      else if (this.form.password.length < 6) this.errors.password = 'Password must be at least 6 characters'

      if (this.form.confirmPassword !== this.form.password)
        this.errors.confirmPassword = 'Passwords do not match'

      if (Object.keys(this.errors).length > 0) return

      this.isLoading = true

      try {
        const { data } = await api.post('/register', {
          username: this.form.username,
          contact_number: phoneClean,
          email: this.form.email,
          campus: this.form.campus,
          fullname: `${this.form.firstName} ${
            this.form.middleInitial ? this.form.middleInitial + ' ' : ''
          }${this.form.lastName}`,
          password: this.form.password
        })

        if (data.success) {
          this.success = 'Registration successful! Your account is pending approval. Redirecting to Sign In...'
          setTimeout(() => this.$router.push({ name: 'SignIn' }), 1500)
        } else {
          this.error = data.message || 'Registration failed'
        }
      } catch (err) {
        console.error('Register error:', err)
        this.error = 'An error occurred during registration'
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style scoped>
.auth-shell__content {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: clamp(2.5rem, 7vw, 5rem) clamp(1.25rem, 4vw, 3.5rem);
}

.auth-form {
  width: min(100%, 700px);
  margin: 0 auto;
  padding: 0;
  max-height: min(88vh, 860px);
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  background: none;
  border: none;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  opacity: 1;
  transform: none;
  animation: none;
  scrollbar-width: thin;
  scrollbar-color: rgba(125, 211, 252, 0.6) rgba(4, 14, 28, 0.15);
}

.auth-form::-webkit-scrollbar {
  width: 10px;
}

.auth-form::-webkit-scrollbar-track {
  background: rgba(4, 14, 28, 0.25);
  border-radius: 12px;
}

.auth-form::-webkit-scrollbar-thumb {
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(125, 211, 252, 0.58), rgba(37, 99, 235, 0.5));
  border: 2px solid rgba(4, 14, 28, 0.4);
}

.auth-form::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, rgba(186, 230, 253, 0.68), rgba(59, 130, 246, 0.58));
}

.auth-form__header .main-title {
  font-size: clamp(2.1rem, 3.8vw, 3rem);
  line-height: 1.05;
  margin-bottom: 0.35rem;
  color: #f8fbff;
  text-shadow: none;
}

.auth-form__header .subtitle {
  color: rgba(186, 201, 230, 0.9);
  margin-top: 0;
}

.form-label {
  color: rgba(214, 226, 250, 0.9);
}

.auth-form .form-input {
  background: rgba(10, 22, 40, 0.72);
  border: 1px solid rgba(83, 112, 160, 0.45);
  color: rgba(235, 242, 255, 0.92);
}

.auth-form .form-input::placeholder {
  color: rgba(186, 201, 230, 0.55);
}

.auth-form .form-input:focus {
  border-color: rgba(56, 189, 248, 0.78);
  box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.24);
  background: rgba(14, 27, 45, 0.9);
}

.password-field {
  position: relative;
  width: 100%;
}

.password-field .form-input {
  width: 100%;
  padding-right: 2.75rem;
}

.password-toggle {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: rgba(226, 232, 255, 0.7);
  cursor: pointer;
  line-height: 0;
  z-index: 2;
}

.password-toggle:hover {
  color: #eff6ff;
}

.password-toggle:focus-visible {
  outline: 2px solid rgba(125, 211, 252, 0.55);
  outline-offset: 2px;
}

.auth-form__links {
  color: rgba(186, 201, 230, 0.72);
}

.auth-form__links a {
  color: #7dd3fc;
  font-weight: 600;
}

.auth-form__links a:hover {
  color: #bae6fd;
}

.custom-button.primary {
  box-shadow: 0 18px 34px rgba(14, 116, 144, 0.35);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.custom-button.primary:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(56, 189, 248, 0.16);
}

.custom-button.primary:active:not(:disabled) {
  transform: translateY(-1px) scale(0.995);
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.18);
  border-top-color: #38bdf8;
  animation: spin 0.9s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  .auth-form,
  .custom-button.primary {
    animation: none !important;
    transition: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
}

@media (max-width: 640px) {
  .auth-shell__content {
    align-items: center;
    padding: clamp(2rem, 9vw, 3rem) clamp(1rem, 6vw, 1.5rem);
  }

  .auth-form {
    max-height: 84vh;
  }

  .auth-form__header .main-title {
    font-size: clamp(1.9rem, 8vw, 2.3rem);
  }
}
</style>

